"use client";

import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Box,
  Chip,
  Button,
  CircularProgress,
  Grid,
  Avatar,
  IconButton,
  Paper,
  Stack,
  Fade,
  useTheme,
  useMediaQuery,
  Container,
  LinearProgress,
} from "@mui/material";
import {
  Info,
  AccessTime,
  Warning,
  Gavel,
  AssignmentLate,
  CheckCircle,
  History,
  Refresh,
  ArrowForward,
  Visibility,
} from "@mui/icons-material";
import DisciplinaryCaseTable from "./DisciplinaryCaseTable";
import DisciplinaryStatusBadge from "./DisciplinaryStatusBadge";
import { getUser } from "../../common/utils";
import { getDisciplinaryCase } from "../../api/disciplinaryService";
import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`case-tabpanel-${index}`}
      aria-labelledby={`case-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const EmployeeDisciplinaryView: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  
  const [loading, setLoading] = useState(true);
  const [disciplinaryCase, setDisciplinaryCase] = useState(null);
  const [error, setError] = useState(null);
  const [activePenalties, setActivePenalties] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDisciplinaryCases = async () => {
    setRefreshing(true);
    try {
      const currentUserId = getUser().userData.user_id;
      const data = await getDisciplinaryCase(currentUserId);
      console.log({ okay: data });
      setDisciplinaryCase(data);

      const active = data?.filter(
        (item) =>
          item.status === "penalty_applied" &&
          (item.penaltyEndDate
            ? new Date(item.penaltyEndDate) > new Date()
            : false)
      );
      setActivePenalties(active);
      setError(null);
    } catch (err) {
      setError("Failed to load disciplinary cases");
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDisciplinaryCases();
  }, []);

  useIonViewWillEnter(() => {
    fetchDisciplinaryCases();
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getPendingResponsesCount = () => {
    return disciplinaryCase?.filter(
      (item) => item.status === "pending" || !item.responseSubmitted
    ).length || 0;
  };

  const getStats = () => {
    const pending = getPendingResponsesCount();
    const active = activePenalties?.length || 0;
    const total = disciplinaryCase?.length || 0;
    const resolved = total - pending - active;

    return { pending, active, total, resolved };
  };

  const stats = getStats();

  const StatCard = ({ icon: Icon, title, value, color, subtitle, loading }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${theme.palette[color]?.main}15 0%, ${theme.palette[color]?.main}05 100%)`,
        border: `1px solid ${theme.palette[color]?.main}20`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px 0 ${theme.palette[color]?.main}15`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ 
            bgcolor: `${theme.palette[color]?.main}20`, 
            color: theme.palette[color]?.main, 
            mr: 2,
            width: 48,
            height: 48
          }}>
            <Icon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {loading ? (
              <LinearProgress sx={{ mt: 1 }} />
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette[color]?.main }}>
                {value}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading && !refreshing) {
    return (
      <IonPage>
        <IonContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
          }}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={60} />
              <Typography variant="h6" color="text.secondary">
                Loading disciplinary cases...
              </Typography>
            </Stack>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ 
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          py: { xs: 2, md: 3 },
          px: { xs: 1, sm: 2 }
        }}>
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
            {/* Header Section */}
            <Card sx={{ 
              mb: 3, 
              background: `linear-gradient(45deg, ${theme.palette.warning.main}15 0%, ${theme.palette.error.main}15 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.warning.main}30`,
              borderRadius: 3,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  flexWrap: 'wrap', 
                  gap: 2 
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      Disciplinary Portal
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8, fontWeight: 400 }}>
                      Manage your disciplinary cases and track ongoing penalties
                    </Typography>
                  </Box>
                  <IconButton 
                    onClick={fetchDisciplinaryCases} 
                    disabled={refreshing}
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      width: 48,
                      height: 48
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>

            {/* Alerts */}
            {(stats.pending > 0 || stats.active > 0) && (
              <Box sx={{ mb: 3 }}>
                <Stack spacing={2}>
                  <Fade in={stats.pending > 0}>
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.warning.main}30`,
                        background: `linear-gradient(45deg, ${theme.palette.warning.main}10 0%, ${theme.palette.warning.main}05 100%)`,
                      }}
                      icon={<Warning />}
                    >
                      <AlertTitle sx={{ fontWeight: 600 }}>Action Required</AlertTitle>
                      You have {stats.pending} pending {stats.pending === 1 ? "query" : "queries"} that require your response.
                    </Alert>
                  </Fade>

                  <Fade in={stats.active > 0}>
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.error.main}30`,
                        background: `linear-gradient(45deg, ${theme.palette.error.main}10 0%, ${theme.palette.error.main}05 100%)`,
                      }}
                    >
                      <AlertTitle sx={{ fontWeight: 600 }}>Active Penalties</AlertTitle>
                      You have {stats.active} active {stats.active === 1 ? "penalty" : "penalties"}. Please review the details below.
                    </Alert>
                  </Fade>
                </Stack>
              </Box>
            )}

            {/* Stats Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={AssignmentLate}
                  title="Pending"
                  value={stats.pending}
                  color="warning"
                  subtitle="Requires your response"
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={Gavel}
                  title="Active Penalties"
                  value={stats.active}
                  color="error"
                  subtitle="Currently applied"
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={CheckCircle}
                  title="Resolved"
                  value={stats.resolved}
                  color="success"
                  subtitle="Cases closed"
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={History}
                  title="Total Cases"
                  value={stats.total}
                  color="info"
                  subtitle="All time"
                  loading={loading}
                />
              </Grid>
            </Grid>

            {/* Active Penalties Section */}
            {stats.active > 0 && (
              <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    background: `linear-gradient(45deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                    px: 3,
                    py: 2
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Active Penalties
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current disciplinary penalties applied to you
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      {activePenalties?.map((penalty) => (
                        <Grid item xs={12} md={6} key={penalty.id}>
                          <Paper 
                            sx={{ 
                              p: 2, 
                              border: `2px solid ${theme.palette.error.main}20`,
                              borderRadius: 2,
                              background: `linear-gradient(45deg, ${theme.palette.error.main}05 0%, transparent 100%)`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: `${theme.palette.error.main}40`,
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {penalty.penaltyType}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Case #{penalty.caseNumber}
                                </Typography>
                              </Box>
                              <DisciplinaryStatusBadge status={penalty.status} />
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                              {penalty.penaltyDescription}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime fontSize="small" color="action" />
                                <Typography variant="caption" color="textSecondary">
                                  Ends {new Date(penalty.penaltyEndDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ArrowForward />}
                                onClick={() => history.push(`/disciplinary/${penalty.id}`)}
                                sx={{ borderRadius: 2 }}
                              >
                                View
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Main Cases Section */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  background: `linear-gradient(45deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    px: 3, 
                    pt: 2,
                    pb: 1
                  }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Disciplinary Cases
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        View and manage your disciplinary cases
                      </Typography>
                    </Box>
                  </Box>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                      '& .MuiTab-root': {
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        minHeight: 48,
                        '&.Mui-selected': { color: 'primary.main' }
                      }
                    }}
                  >
                    <Tab 
                      icon={<AssignmentLate sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Pending
                          {stats.pending > 0 && (
                            <Chip
                              label={stats.pending}
                              color="warning"
                              size="small"
                              sx={{ minWidth: 20, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <Tab 
                      icon={<History sx={{ fontSize: 18 }} />}
                      iconPosition="start"
                      label="All Cases"
                    />
                  </Tabs>
                </Box>

                <Box sx={{ p: 3 }}>
                  <TabPanel value={tabValue} index={0}>
                    <DisciplinaryCaseTable
                      cases={disciplinaryCase?.filter(
                        (item) =>
                          item.status === "pending" && !item.responseSubmitted
                      )}
                      loading={refreshing}
                      error={error}
                      isEmployeeView={true}
                    />
                  </TabPanel>

                  <TabPanel value={tabValue} index={1}>
                    <DisciplinaryCaseTable
                      cases={disciplinaryCase}
                      loading={refreshing}
                      error={error}
                      isEmployeeView={true}
                    />
                  </TabPanel>
                </Box>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card sx={{ mt: 3, borderRadius: 3, background: `linear-gradient(45deg, ${theme.palette.info.main}05 0%, transparent 100%)` }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <Info sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      About Disciplinary Procedures
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The disciplinary process is designed to address workplace issues and improve performance. 
                      When you receive a query, you have 48 hours to respond. Your response will be reviewed by 
                      management before any decisions are made. If you have questions about the process, please contact HR.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default EmployeeDisciplinaryView;
